import * as React from 'react';
import {createViewConfig, view} from '@sail/engine';
import {Link, LinkProps} from '@sail/ui';
import type {View} from '@sail/engine';
import {useRoute} from '@sail/navigation';
import {styles} from 'mkt-ssr/components/sail/GlobalizationPicker/GlobalizationPickerStyles';
import {getPrefixForPair} from 'mkt-ssr/lib/globalization/helpers';
import {PathPrefixes} from 'mkt-ssr/generated/globalization/pathPrefixes';
import type {Country, Locale} from 'mkt-ssr/lib/globalization/types';
import {useLinkResolver} from 'mkt-ssr/lib/routing/LinkResolver';
import {Cookies} from '@stripe-internal/stripe-cookies';

export type GlobalizationPickerCountryLanguageProps = LinkProps & {
  /**
   * Whether this language is the active globalization language
   */
  isActive: boolean;
  /**
   * The locale data for this language
   */
  locale: Locale;
  /**
   * The display name for this language (e.g. "English", "Español",  etc.)
   */
  displayName: string;
  /**
   * The two letter code for this language's country (e.g. US, CA, etc.)
   */
  country: Country;
  /**
   * The prefix to use for the test id
   */
  testIdPrefix?: string;

  subviews?: View.Subviews<{
    listItem: 'li';
  }>;
};

export const GlobalizationPickerCountryLanguageConfig = createViewConfig({
  props: {} as GlobalizationPickerCountryLanguageProps,
  name: 'GlobalizationPickerCountryLanguage',
});

/**
 * Renders the individual language list item for the GlobalizationPickerCountry
 */
export const GlobalizationPickerCountryLanguage =
  GlobalizationPickerCountryLanguageConfig.createView((props) => {
    const {
      isActive,
      locale,
      displayName,
      country,
      testIdPrefix,
      subviews,
      ...rest
    } = props;

    const [route] = useRoute();
    const {createRoute} = useLinkResolver();
    const globalizationPair = Object.values(PathPrefixes).find((pathPrefix) => {
      const isLocaleMatch = pathPrefix.locale.code === locale.code;
      const isCountryMatch = pathPrefix.country.code === country.code;

      return isCountryMatch && isLocaleMatch;
    });
    const globalizationFound = globalizationPair !== undefined;
    if (!globalizationFound) return null;

    const href = createRoute({
      name: route.name as keyof SailNavigationRouteParams,
      params: {
        ...route.params,
        localized: {
          locale: getPrefixForPair(globalizationPair),
        },
      },
      hash: route.hash,
      searchSafe: route.searchSafe,
    });

    return (
      <view.li inherits={subviews.listItem}>
        <Link
          href={href}
          onClick={() => {
            const cookies = new Cookies();
            if (country && locale) {
              // stripe-cookies.js currently doesn't automatically handle dev domains,
              // so we need to do this or it won't work on devboxes:
              const domain = window.location.hostname.match('.dev.stripe.me')
                ? '.dev.stripe.me'
                : '.stripe.com';

              cookies.set('country', country.code, {domain});
              cookies.set('locale', locale.code, {domain});
            }
          }}
          uses={[
            styles.countryLanguage,
            isActive && styles.activeCountryLanguage,
          ]}
          data-globalization-active={isActive}
          data-testid={`${testIdPrefix}-${country.code}-${locale.code}-link`}
          {...rest}
        >
          {displayName}
        </Link>
      </view.li>
    );
  }, {});
